'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';


interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface LoginFormProps {
  onSwitchToSignUp: () => void;
}

const demoCredentials = [
  { role: 'Pro User', email: 'markus.weber@smartemailhub.de', password: 'Hub2026!Secure' },
  { role: 'Team Admin', email: 'admin@contoso-hub.de', password: 'TeamAdmin#2026' },
];

export default function LoginForm({ onSwitchToSignUp }: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormData>({ defaultValues: { rememberMe: false } });

  const onSubmit = async (data: LoginFormData) => {
    const valid = demoCredentials.some(
      (c) => c.email === data.email && c.password === data.password
    );
    if (!valid) {
      toast.error('Ungültige Anmeldedaten — nutze die Demo-Konten unten zum Anmelden.');
      return;
    }
    setLoading(true);
    // TODO: Backend integration — POST /api/auth/login with { email, password, rememberMe }
    await new Promise((r) => setTimeout(r, 1400));
    setLoading(false);
    toast.success('Willkommen zurück!');
    window.location.href = '/';
  };

  return (
    <div className="space-y-5">
      <div className="text-center mb-2">
        <h1 className="text-2xl font-bold text-foreground mb-1">Willkommen zurück</h1>
        <p className="text-sm text-muted-foreground">Melde dich bei deinem SmartEmailHub an</p>
      </div>

      {/* OAuth buttons */}
      <div className="space-y-2.5">
        <button className="w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-xl border border-border bg-card hover:bg-muted transition-all duration-150 text-sm font-semibold text-foreground active:scale-95">
          <div className="w-5 h-5 rounded bg-red-500 flex items-center justify-center text-white text-xs font-bold">G</div>
          Mit Google anmelden
        </button>
        <button className="w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-xl border border-border bg-card hover:bg-muted transition-all duration-150 text-sm font-semibold text-foreground active:scale-95">
          <div className="w-5 h-5 rounded bg-blue-600 flex items-center justify-center text-white text-xs font-bold">M</div>
          Mit Microsoft anmelden
        </button>
      </div>

      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs text-muted-foreground font-medium">oder per E-Mail</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <div>
          <label htmlFor="login-email" className="label-text">E-Mail-Adresse</label>
          <input
            id="login-email"
            type="email"
            autoComplete="email"
            placeholder="deine@email.de"
            className={`input-field ${errors.email ? 'border-red-400 focus:ring-red-400' : ''}`}
            {...register('email', {
              required: 'E-Mail ist erforderlich.',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Ungültige E-Mail-Adresse.' },
            })}
          />
          {errors.email && <p className="error-text"><Icon name="ExclamationCircleIcon" size={12} variant="outline" />{errors.email.message}</p>}
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label htmlFor="login-password" className="label-text mb-0">Passwort</label>
            <a href="#" className="text-xs text-primary hover:underline font-medium">Vergessen?</a>
          </div>
          <div className="relative">
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="••••••••"
              className={`input-field pr-10 ${errors.password ? 'border-red-400 focus:ring-red-400' : ''}`}
              {...register('password', {
                required: 'Passwort ist erforderlich.',
                minLength: { value: 6, message: 'Mindestens 6 Zeichen.' },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
            >
              <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} variant="outline" />
            </button>
          </div>
          {errors.password && <p className="error-text"><Icon name="ExclamationCircleIcon" size={12} variant="outline" />{errors.password.message}</p>}
        </div>

        <div className="flex items-center gap-2">
          <input
            id="rememberMe"
            type="checkbox"
            className="w-4 h-4 rounded border-border text-primary focus:ring-ring"
            {...register('rememberMe')}
          />
          <label htmlFor="rememberMe" className="text-sm text-foreground cursor-pointer">Angemeldet bleiben</label>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="btn-primary w-full justify-center py-3 text-sm"
        >
          {loading ? (
            <>
              <Icon name="ArrowPathIcon" size={16} variant="outline" className="animate-spin" />
              Anmelden…
            </>
          ) : (
            'Anmelden'
          )}
        </button>
      </form>

      {/* Demo credentials box */}
      <div className="rounded-xl border border-blue-200 bg-secondary p-4 space-y-2">
        <p className="text-xs font-semibold text-primary flex items-center gap-1.5">
          <Icon name="KeyIcon" size={13} variant="outline" />
          Demo-Zugangsdaten
        </p>
        {demoCredentials.map((cred) => (
          <div key={`demo-${cred.role}`} className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <span className="badge bg-blue-100 text-blue-700 text-2xs mb-0.5">{cred.role}</span>
              <p className="text-xs font-mono-data text-foreground truncate">{cred.email}</p>
            </div>
            <button
              type="button"
              onClick={() => {
                setValue('email', cred.email);
                setValue('password', cred.password);
                toast.success(`${cred.role} Zugangsdaten eingefügt`);
              }}
              className="flex-shrink-0 btn-secondary text-xs py-1.5 px-2.5"
            >
              Verwenden
            </button>
          </div>
        ))}
      </div>

      <p className="text-center text-sm text-muted-foreground">
        Noch kein Konto?{' '}
        <button onClick={onSwitchToSignUp} className="text-primary font-semibold hover:underline">
          Jetzt registrieren
        </button>
      </p>
    </div>
  );
}